/**
 * @fileoverview Task details section (MOR-696)
 *
 * The project-management fields of a Morgan key date / task, rendered inside
 * the key-date side panel: status, priority, assignee + assignor, planned
 * date, parent task, subtask progress (n/m + complete-all), and labels.
 *
 * Reads the firm's statuses/labels and org members live, and the job's other
 * events (for parent/subtask wiring). Every edit calls the task mutations,
 * which materialise derived statutory rows on first touch and write change
 * history.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { Link } from "@tanstack/react-router";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  ArrowUpRight,
  CheckCircle2,
  Flag,
  Layers,
  ListTodo,
  ListTree,
  Plus,
  Settings2,
  Shapes,
  Tag,
  UserCheck,
  UserPlus,
} from "lucide-react";

import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { getDisplayName, UserAvatar } from "@/ui/composed";
import { QuickAddStatusDialog } from "@/components/settings/tasks/QuickAddStatusDialog";
import { QuickAddLabelDialog } from "@/components/settings/tasks/QuickAddLabelDialog";
import { useActiveOrg } from "@/utils/org-context";
import { extractErrorMessage } from "@/utils/errors";

import { isEventKind, type CalendarEventRow } from "@/calendar/eventAdapter";
import {
  CATEGORY_META,
  TASK_CATEGORY_ORDER,
  isKeyDateCategory,
} from "@/calendar/categoryMeta";
import { TASK_PRIORITIES, priorityMeta } from "@/calendar/task-priority";
import { groupStatusesByStage } from "@/calendar/task-status-order";
import { useTaskPanelNav } from "@/calendar/task-panel-nav";
import { cn } from "@/utils/misc";

const NONE = "__none__";

/** Either an existing persisted id, or a (jobId, code) pair to materialise. */
type TaskTarget =
  { id: Id<"jobKeyDates"> } | { jobId: Id<"jobs">; code: string } | null;

function resolveTarget(event: CalendarEventRow): TaskTarget {
  if (event.persistedId) return { id: event.persistedId };
  if (event.code) return { jobId: event.jobId, code: event.code };
  return null;
}

export function TaskDetailsSection({ event }: { event: CalendarEventRow }) {
  const statuses = useQuery(api.tasks.statuses.listByOrg, {
    orgId: event.orgId,
  });
  const labels = useQuery(api.tasks.labels.listByOrg, { orgId: event.orgId });
  const members = useQuery(api.organizations.queries.getOrganizationMembers, {
    orgId: event.orgId,
  });
  const jobEvents = useQuery(api.keyDates.queries.listForJob, {
    jobId: event.jobId,
  });

  const updateTask = useMutation(api.tasks.mutations.updateTask);
  const setTaskStatus = useMutation(api.tasks.mutations.setTaskStatus);
  const completeAllSubtasks = useMutation(
    api.tasks.mutations.completeAllSubtasks,
  );
  const materialise = useMutation(api.keyDates.mutations.materialise);
  const updateKeyDate = useMutation(api.keyDates.mutations.update);

  const nav = useTaskPanelNav();
  const { activeOrg } = useActiveOrg();
  const canManageTaskSettings =
    activeOrg?.role === "owner" || activeOrg?.role === "admin";

  const [statusOpen, setStatusOpen] = useState(false);
  const [addStatusOpen, setAddStatusOpen] = useState(false);
  const [addLabelOpen, setAddLabelOpen] = useState(false);

  const target = resolveTarget(event);

  // ---- option lists ------------------------------------------------------
  const memberOptions = useMemo(
    () =>
      (members ?? []).map((m) => ({
        value: m.user._id as string,
        label: getDisplayName(m.user.firstName, m.user.lastName, m.user.email),
        avatar: (
          <UserAvatar
            avatarUrl={m.user.avatarUrl}
            firstName={m.user.firstName}
            lastName={m.user.lastName}
            email={m.user.email}
            className="size-4 shrink-0 text-[8px]"
            fallbackClassName="text-[8px]"
          />
        ),
      })),
    [members],
  );

  const labelOptions = useMemo(
    () =>
      (labels ?? []).map((l) => ({
        value: l._id as string,
        label: l.name,
        color: l.color,
      })),
    [labels],
  );

  // Any other row on the job can be the parent — including derived statutory
  // rows that have never been persisted. Those carry a `derived::<code>` value
  // and are materialised on selection (MOR-707). Split by type so tasks show a
  // "Parent task" picker and an "Associate with event" picker separately.
  const parentCandidates = useMemo(
    () =>
      (jobEvents ?? []).filter(
        (e) =>
          e.id !== event.id &&
          (e.persistedId || e.code) &&
          (!e.persistedId ||
            (e.persistedId !== event.persistedId &&
              e.parentTaskId !== event.persistedId)),
      ),
    [jobEvents, event.id, event.persistedId],
  );

  const toParentOption = (e: CalendarEventRow) => ({
    value: e.persistedId ? (e.persistedId as string) : `derived::${e.code}`,
    label: e.title,
  });

  // A task's parent can be either another task or an event (per spec).
  const allParentOptions = useMemo(
    () => parentCandidates.map(toParentOption),
    [parentCandidates],
  );

  /** Resolve a parent-picker value to a persisted id, materialising derived rows. */
  const resolveParentId = async (value: string): Promise<Id<"jobKeyDates">> => {
    if (value.startsWith("derived::")) {
      return await materialise({
        jobId: event.jobId,
        code: value.slice("derived::".length),
      });
    }
    return value as Id<"jobKeyDates">;
  };

  const subtasks = useMemo(
    () =>
      event.persistedId
        ? (jobEvents ?? []).filter((e) => e.parentTaskId === event.persistedId)
        : [],
    [jobEvents, event.persistedId],
  );

  // The full parent row (when set) so the practitioner can jump into it.
  const parentRow = useMemo(
    () =>
      event.parentTaskId
        ? (jobEvents ?? []).find((e) => e.persistedId === event.parentTaskId)
        : undefined,
    [jobEvents, event.parentTaskId],
  );

  const isEvent = isEventKind(event);

  // ---- handlers ----------------------------------------------------------
  // `fn` receives the narrowed (non-null) target directly, since narrowing
  // `target` in the outer scope does not carry across the closure boundary.
  const guarded = async (
    fn: (t: NonNullable<TaskTarget>) => Promise<unknown>,
    fallback: string,
  ) => {
    if (!target) return;
    try {
      await fn(target);
    } catch (e) {
      toast.error(extractErrorMessage(e, fallback));
    }
  };

  const onStatus = (statusId: string) =>
    guarded(
      (t) =>
        setTaskStatus({
          ...t,
          statusId: statusId as Id<"taskStatuses">,
        }),
      "Could not update status.",
    );

  const onPatch = (patch: Record<string, unknown>, fallback: string) =>
    guarded(
      (t) => updateTask({ ...t, patch } as Parameters<typeof updateTask>[0]),
      fallback,
    );

  // Parent / event association both write the single `parentTaskId` slot,
  // materialising derived candidates on selection.
  const setParent = (v: string) =>
    guarded(async (t) => {
      const parentTaskId = v === NONE ? null : await resolveParentId(v);
      await updateTask({
        ...t,
        patch: { parentTaskId },
      } as Parameters<typeof updateTask>[0]);
    }, "Could not update the association.");

  // "Add subtask" opens a create column pre-parented to this row. A subtask
  // needs a persisted parent id, so derived statutory rows are materialised
  // first (the same promotion that setting a status/planned date performs).
  const onAddSubtask = () =>
    guarded(async () => {
      if (!nav) return;
      if (event.persistedId) {
        nav.openCreateChild(event);
        return;
      }
      if (event.code) {
        const id = await materialise({ jobId: event.jobId, code: event.code });
        nav.openCreateChild({ ...event, persistedId: id });
      }
    }, "Could not add a subtask.");

  const currentPriority = priorityMeta(event.priority);
  const PriorityIcon = currentPriority.icon;

  return (
    <div className="space-y-4">
      <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
        Details
      </h4>

      {/* Type — Event vs Task. User-created persisted rows only; statutory
          rows (a `code`) are pinned to the event treatment, so no toggle. */}
      {event.persistedId && !event.isDerived && !event.code && (
        <div className="grid grid-cols-[110px_1fr] items-center gap-2">
          <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Layers className="size-3.5" />
            Type
          </Label>
          <Select
            value={isEvent ? "event" : "task"}
            onValueChange={(v) => {
              // This block only renders when `event.persistedId` is set (see
              // the surrounding condition above), but that narrowing does not
              // survive into this nested closure, so re-check here.
              const persistedId = event.persistedId;
              if (!persistedId) return;
              guarded(
                () =>
                  updateKeyDate({
                    id: persistedId,
                    kind: v as "event" | "task",
                  }),
                "Could not update type.",
              );
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="event" className="text-xs">
                Event
              </SelectItem>
              <SelectItem value="task" className="text-xs">
                Task
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Category — event-only (per spec). */}
      {isEvent && (
        <div className="grid grid-cols-[110px_1fr] items-center gap-2">
          <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Shapes className="size-3.5" />
            Category
          </Label>
          <Select
            value={event.category}
            onValueChange={(v) => {
              if (!isKeyDateCategory(v)) return;
              onPatch({ category: v }, "Could not update category.");
            }}
            disabled={!target}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Set category" />
            </SelectTrigger>
            <SelectContent>
              {TASK_CATEGORY_ORDER.map((c) => {
                const meta = CATEGORY_META[c];
                const Icon = meta.icon;
                return (
                  <SelectItem key={c} value={c} className="text-xs">
                    <span className="flex items-center gap-2">
                      <Icon className={cn("size-3.5", meta.iconFgClass)} />
                      {meta.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Status — events + tasks. Events get a pared-back PM field set
          (status / priority / labels), but not assignee / parent. */}
      <div className="grid grid-cols-[110px_1fr] items-center gap-2">
        <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5" />
          Status
        </Label>
        <Select
          value={event.statusId ?? ""}
          onValueChange={onStatus}
          open={statusOpen}
          onOpenChange={setStatusOpen}
          disabled={!statuses}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Set status" />
          </SelectTrigger>
          <SelectContent>
            {/* Flat status list — stage grouping is hidden for now. */}
            {groupStatusesByStage(statuses ?? [], []).map((g) => (
              <SelectGroup key={g.key}>
                {g.label && <SelectLabel>{g.label}</SelectLabel>}
                {g.statuses.map((s) => (
                  <SelectItem key={s._id} value={s._id}>
                    <span className="flex items-center gap-2">
                      <Badge color={s.color} variant="soft" size="sm">
                        {s.name}
                      </Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}

            {canManageTaskSettings && (
              <>
                <SelectSeparator />
                <div className="flex flex-col gap-0.5 p-1">
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setStatusOpen(false);
                      // Defer so the Select popover unmounts before the dialog
                      // grabs focus, avoiding a focus tug-of-war.
                      setTimeout(() => setAddStatusOpen(true), 0);
                    }}
                  >
                    <Plus className="size-3.5" />
                    New status
                  </button>
                  <Link
                    to="/dashboard/task-statuses"
                    onClick={() => setStatusOpen(false)}
                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Settings2 className="size-3.5" />
                    Manage statuses
                  </Link>
                </div>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {canManageTaskSettings && (
        <QuickAddStatusDialog
          orgId={event.orgId}
          open={addStatusOpen}
          onOpenChange={setAddStatusOpen}
          onCreated={(statusId) => {
            // Apply the newly created status to this row when possible.
            if (target) onStatus(statusId);
          }}
        />
      )}

      {canManageTaskSettings && (
        <QuickAddLabelDialog
          orgId={event.orgId}
          open={addLabelOpen}
          onOpenChange={setAddLabelOpen}
          onCreated={(labelId) => {
            // Add the newly created label to this row when possible.
            if (target) {
              onPatch(
                {
                  labelIds: [
                    ...(event.labelIds as Array<Id<"taskLabels">>),
                    labelId,
                  ],
                },
                "Could not update labels.",
              );
            }
          }}
        />
      )}

      {/* Priority — events + tasks. */}
      <div className="grid grid-cols-[110px_1fr] items-center gap-2">
        <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Flag className="size-3.5" />
          Priority
        </Label>
        <Select
          value={String(event.priority ?? 0)}
          onValueChange={(v) =>
            onPatch({ priority: Number(v) }, "Could not update priority.")
          }
          disabled={!target}
        >
          <SelectTrigger className="h-8 text-xs">
            <span className="flex items-center gap-2">
              <PriorityIcon className="size-3.5" />
              {currentPriority.label}
            </span>
          </SelectTrigger>
          <SelectContent>
            {TASK_PRIORITIES.map((p) => {
              const Icon = p.icon;
              return (
                <SelectItem
                  key={p.value}
                  value={String(p.value)}
                  className="text-xs"
                >
                  <span className="flex items-center gap-2">
                    <Icon className="size-3.5" />
                    {p.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Assignee / Assigned by / Parent — shown for both events and tasks.
          Some events (e.g. a ROCAP lodgement) are worked like tasks, so they
          need the same assignment + parent wiring. */}
      {/* Assignee */}
      <div className="grid grid-cols-[110px_1fr] items-center gap-2">
        <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <UserPlus className="size-3.5" />
          Assignee
        </Label>
        <SearchableSelect
          value={event.assigneeUserId ?? NONE}
          onValueChange={(v) =>
            onPatch(
              { assigneeUserId: v === NONE ? null : (v as Id<"users">) },
              "Could not update assignee.",
            )
          }
          options={memberOptions}
          allOptionLabel="Unassigned"
          allOptionValue={NONE}
          placeholder="Unassigned"
          searchPlaceholder="Search members…"
          className="h-8"
        />
      </div>

      {/* Assignor ("assigned by") — editable */}
      <div className="grid grid-cols-[110px_1fr] items-center gap-2">
        <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <UserCheck className="size-3.5" />
          Assigned by
        </Label>
        <SearchableSelect
          value={event.assignorUserId ?? NONE}
          onValueChange={(v) =>
            onPatch(
              { assignorUserId: v === NONE ? null : (v as Id<"users">) },
              "Could not update who assigned this.",
            )
          }
          options={memberOptions}
          allOptionLabel="Nobody"
          allOptionValue={NONE}
          placeholder="Nobody"
          searchPlaceholder="Search members…"
          className="h-8"
        />
      </div>

      {/* Parent — a task or an event (writes the single `parentTaskId`). */}
      <div className="grid grid-cols-[110px_1fr] items-center gap-2">
        <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ListTree className="size-3.5" />
          Parent
        </Label>
        <div className="flex items-center gap-1.5">
          <SearchableSelect
            value={event.parentTaskId ?? NONE}
            onValueChange={setParent}
            options={allParentOptions}
            allOptionLabel="No parent"
            allOptionValue={NONE}
            placeholder="No parent"
            searchPlaceholder="Search tasks & events…"
            className="h-8 min-w-0 flex-1"
          />
          {parentRow && nav && (
            <Button
              size="sm"
              variant="ghost"
              className="size-8 shrink-0 px-0 text-muted-foreground"
              title={`Open ${parentRow.title}`}
              onClick={() => nav.openRelated(parentRow)}
            >
              <ArrowUpRight className="size-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Labels — events + tasks. */}
      <div className="grid grid-cols-[110px_1fr] items-center gap-2">
        <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Tag className="size-3.5" />
          Labels
        </Label>
        <MultiSearchableSelect
          value={event.labelIds as string[]}
          onValueChange={(ids) =>
            onPatch(
              { labelIds: ids as Array<Id<"taskLabels">> },
              "Could not update labels.",
            )
          }
          options={labelOptions}
          placeholder="No labels"
          searchPlaceholder="Search labels…"
          className="h-8"
          footer={
            canManageTaskSettings
              ? ({ close }) => (
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      onClick={() => {
                        close();
                        // Defer so the popover unmounts before the dialog
                        // grabs focus, avoiding a focus tug-of-war.
                        setTimeout(() => setAddLabelOpen(true), 0);
                      }}
                    >
                      <Plus className="size-3.5" />
                      New label
                    </button>
                    <Link
                      to="/dashboard/task-labels"
                      onClick={close}
                      className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <Settings2 className="size-3.5" />
                      Manage labels
                    </Link>
                  </div>
                )
              : undefined
          }
        />
      </div>

      {/* Subtasks — "Add subtask" opens a full create panel pre-parented to
          this row, so the child can be authored with all its details. Derived
          rows show it too and materialise on first "Add subtask". */}
      {(event.persistedId || event.code) && (
        <div className="space-y-2 rounded-md border p-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ListTodo className="size-3.5" />
              Subtasks ({event.subtaskCompleted}/{event.subtaskTotal})
            </span>
            {event.subtaskCompleted < event.subtaskTotal && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  // This button only renders when there is subtask progress
                  // to complete, which implies a persisted parent, but that
                  // narrowing does not survive into this nested closure.
                  const parentId = event.persistedId;
                  if (!parentId) return;
                  guarded(
                    () => completeAllSubtasks({ parentId }),
                    "Could not complete subtasks.",
                  );
                }}
              >
                Complete all
              </Button>
            )}
          </div>
          {subtasks.length > 0 && (
            <ul className="space-y-1 pb-1">
              {subtasks.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <CheckCircle2
                    className={
                      s.statusType === "completed"
                        ? "size-3.5 text-emerald-500"
                        : "size-3.5 text-muted-foreground/40"
                    }
                  />
                  {nav ? (
                    <button
                      type="button"
                      onClick={() => nav.openRelated(s)}
                      className={
                        "min-w-0 flex-1 truncate text-left hover:text-primary hover:underline " +
                        (s.statusType === "completed"
                          ? "text-muted-foreground line-through"
                          : "")
                      }
                      title={`Open ${s.title}`}
                    >
                      {s.title}
                    </button>
                  ) : (
                    <span
                      className={
                        s.statusType === "completed"
                          ? "text-muted-foreground line-through"
                          : ""
                      }
                    >
                      {s.title}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {nav && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-full justify-center gap-1.5 text-xs"
              onClick={onAddSubtask}
            >
              <Plus className="size-3.5" />
              Add subtask
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
